import React from 'react';
import { MessageCircle } from 'lucide-react';
import AgencyNavbar from '../components/layout/AgencyNavbar';
import AgencyFooter from '../components/layout/AgencyFooter';
import { useSiteContent } from '../contexts/SiteContentContext';

const AgencyLanding = () => {
  const { getPageContent, settings } = useSiteContent();
  const content = getPageContent('home') || {};

  const hero = content.hero || {};
  const features = content.features || {};
  const cta = content.cta || {};

  const brandParts = (settings.brand_name || 'mispedidos app').toUpperCase().split(' ');

  return (
    <div className="min-h-screen bg-black text-white">
      <AgencyNavbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 pt-16 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1770892393329-d0c76e77fc0c?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Hero"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
        </div>

        <div className="relative z-10 text-center w-full max-w-3xl mx-auto px-6">
          {/* Main title */}
          <h1
            className="font-black tracking-tighter uppercase mb-8 leading-none text-center"
            style={{
              fontFamily: 'Unbounded, Montserrat, sans-serif',
              fontSize: 'clamp(2.2rem, 10vw, 5.5rem)'
            }}
            data-testid="agency-title"
          >
            <span className="text-[#E9FF60]">{hero.title_1 || brandParts[0] || 'MIS'}</span>
            <span className="text-white">{hero.title_2 || brandParts[1] || 'PEDIDOS'}</span>
            {(hero.title_3 || brandParts[2]) && (
              <span className="text-[#E9FF60]"> {hero.title_3 || brandParts[2]}</span>
            )}
          </h1>

          {/* Taglines */}
          <div className="space-y-3 mb-10 text-center">
            <p
              className="font-bold text-[#E9FF60] text-center"
              style={{ fontSize: 'clamp(0.9rem, 3.5vw, 1.3rem)' }}
              data-testid="agency-tagline-1"
            >
              {hero.tagline_1 || 'Hackeá las comisiones de otras apps.'}
            </p>
            <p
              className="text-white text-center"
              style={{ fontSize: 'clamp(0.85rem, 3vw, 1.1rem)' }}
              data-testid="agency-tagline-2"
            >
              {hero.tagline_2 || 'Reducí costos y controlá tu propia base de datos'}
            </p>
            <p
              className="text-white text-center"
              style={{ fontSize: 'clamp(0.85rem, 3vw, 1.1rem)' }}
              data-testid="agency-tagline-3"
            >
              {hero.tagline_3 || 'Automatizá y vendé más sin intermediarios'}
            </p>
            <p
              className="text-gray-400 mt-5 text-center"
              style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}
              data-testid="agency-question"
            >
              {hero.question || '¿Querés esta app para tu negocio?'}
            </p>
          </div>

          {/* CTA Button */}
          <a
            href={hero.cta_link || `https://wa.me/${settings.whatsapp_number || '3425149602'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#FF3131] hover:bg-[#ff4545] active:bg-[#cc2020] text-white rounded-full font-bold shadow-2xl transition-all duration-200 hover:scale-105"
            style={{ fontSize: 'clamp(0.95rem, 3.5vw, 1.25rem)', padding: 'clamp(0.85rem, 3vw, 1.25rem) clamp(1.5rem, 6vw, 2.5rem)' }}
            data-testid="agency-cta-button"
          >
            <MessageCircle className="w-5 h-5 flex-shrink-0" />
            <span>{hero.cta_text || 'Hablá con un asesor'}</span>
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 bg-[#1C1C1C]">
        <div className="max-w-6xl mx-auto">
          <h2
            className="font-black mb-12 md:mb-16 text-center text-[#E9FF60]"
            style={{ fontSize: 'clamp(1.6rem, 6vw, 3rem)' }}
            data-testid="features-title"
          >
            {features.title || `¿POR QUÉ ${(settings.brand_name || 'MISPEDIDOS APP').toUpperCase()}?`}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {(features.items || [
              { emoji: '💰', title: '0% de comisión', description: 'Dejá de perder hasta un 30% de tus ventas en comisiones. Todo el dinero es tuyo.' },
              { emoji: '📊', title: 'Tu base de datos', description: 'Tus clientes son tuyos. Conocé sus preferencias y fidelizalos.' },
              { emoji: '⚡', title: 'Setup en minutos', description: 'Tu app personalizada lista en menos de 24 horas. Sin complicaciones.' }
            ]).map((feature, i) => (
              <div
                key={i}
                className="bg-black p-6 md:p-8 rounded-xl border border-[#333333] hover:border-[#E9FF60] transition-colors"
                data-testid={`feature-${i + 1}`}
              >
                <div className="text-4xl mb-4">{feature.emoji}</div>
                <h3
                  className="font-bold mb-3 text-[#E9FF60]"
                  style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)' }}
                >
                  {feature.title}
                </h3>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="font-black mb-8 text-white"
            style={{ fontSize: 'clamp(1.6rem, 6vw, 3rem)' }}
            data-testid="cta-title"
          >
            {cta.title?.replace(cta.highlight || 'CONTROL', '') || '¿LISTO PARA TOMAR EL '}
            <span className="text-[#FF3131]">{cta.highlight || 'CONTROL'}</span>?
          </h2>
          <a
            href={cta.button_link || '/demo'}
            className="inline-block bg-[#E9FF60] hover:bg-[#f0ff80] active:bg-[#d4e840] text-black rounded-full font-bold transition-all duration-200 hover:scale-105"
            style={{ fontSize: 'clamp(1rem, 3.5vw, 1.25rem)', padding: 'clamp(0.85rem, 3vw, 1.25rem) clamp(2rem, 8vw, 3rem)' }}
            data-testid="cta-final-button"
          >
            {cta.button_text || 'Empezá Ahora'}
          </a>
        </div>
      </section>

      <AgencyFooter />
    </div>
  );
};

export default AgencyLanding;
